<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  isScrolled: {
    type: Boolean,
    default: false
  }
});

const route = useRoute();
const mobileMenuOpen = ref(false);
const isHomePage = ref(true);

const navigation = [
  { name: '首页', href: '/' },
  { name: '公司介绍', href: '/about' },
  { name: '解决方案', href: '/solutions' },
  { name: '项目案例', href: '/projects' },
  { name: '加入我们', href: '/careers' },
  { name: '联系我们', href: '/contact' },
];

// Close mobile menu when route changes
const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
};

// Update isHomePage when route changes
const updateIsHomePage = () => {
  isHomePage.value = route.path === '/';
};

// Add event listeners
onMounted(() => {
  updateIsHomePage();
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});

// Close mobile menu on larger screens
const handleResize = () => {
  if (window.innerWidth >= 768) {
    mobileMenuOpen.value = false;
  }
};

// Computed class for header
const headerClasses = computed(() => ({
  'bg-white shadow-lg':  mobileMenuOpen.value || !isHomePage.value,
  'bg-transparent':  !mobileMenuOpen.value && isHomePage.value,
  'backdrop-blur-sm': isHomePage.value && !mobileMenuOpen.value,
  'bg-white/90': isHomePage.value &&  !mobileMenuOpen.value,
}));

// Computed class for nav items
const navItemClasses = (isActive) => ({
  'text-primary-600 font-semibold': isActive,
  'text-gray-700 hover:text-primary-600': !isActive,
  'transition-colors duration-200': true,
  'relative group': true,
});

// Computed class for mobile nav items
const mobileNavItemClasses = (isActive) => ({
  'text-primary-600 bg-primary-50': isActive,
  'text-gray-700 hover:bg-gray-50': !isActive,
  'block px-4 py-3 rounded-lg transition-colors duration-200': true,
});
</script>

<template>
  <header 
    class="fixed w-full z-50 transition-all duration-300"
    :class="headerClasses"
  >
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <nav class="flex items-center justify-between h-20" aria-label="Global">
        <!-- Logo -->
        <div class="flex items-center flex-shrink-0">
          <router-link 
            to="/" 
            class="flex items-center group"
            @click="closeMobileMenu"
          >
            <span class="text-2xl font-extrabold bg-gradient-to-r from-primary-600 to-primary-400 bg-clip-text text-transparent">
              维域科技
            </span>
            <span class="ml-2 text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
              Weiyu Tech
            </span>
          </router-link>
        </div>

        <!-- Desktop Navigation -->
        <div class="hidden md:flex items-center space-x-1">
          <router-link
            v-for="item in navigation"
            :key="item.name"
            :to="item.href"
            class="px-4 py-2.5 text-sm font-medium rounded-lg mx-1"
            :class="navItemClasses(route.path === item.href)"
          >
            {{ item.name }}
            <span 
              v-if="route.path === item.href" 
              class="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-primary-600 rounded-full mt-1"
            ></span>
          </router-link>
          <router-link
            to="/contact"
            class="ml-4 btn btn-primary btn-sm whitespace-nowrap"
            @click="closeMobileMenu"
          >
            联系我们
          </router-link>
        </div>

        <!-- Mobile menu button -->
        <div class="md:hidden">
          <button
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="inline-flex items-center justify-center p-2 rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
            :aria-expanded="mobileMenuOpen"
          >
            <span class="sr-only">{{ mobileMenuOpen ? '关闭菜单' : '打开菜单' }}</span>
            <Bars3Icon v-if="!mobileMenuOpen" class="h-6 w-6" aria-hidden="true" />
            <XMarkIcon v-else class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </nav>
    </div>

    <!-- Mobile menu -->
    <transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0 -translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-4"
    >
      <div 
        v-if="mobileMenuOpen" 
        class="md:hidden bg-white shadow-xl rounded-xl mx-4 mt-2 p-4 border border-gray-100"
      >
        <div class="space-y-1">
          <router-link
            v-for="item in navigation"
            :key="item.name"
            :to="item.href"
            @click="closeMobileMenu"
            class="text-base font-medium flex items-center"
            :class="mobileNavItemClasses(route.path === item.href)"
          >
            <span>{{ item.name }}</span>
            <svg 
              v-if="route.path === item.href" 
              class="ml-2 h-4 w-4 text-primary-600" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </router-link>
        </div>
        <div class="mt-4 pt-4 border-t border-gray-100">
          <router-link
            to="/contact"
            class="w-full btn btn-primary flex items-center justify-center"
            @click="closeMobileMenu"
          >
            联系我们
          </router-link>
        </div>
      </div>
    </transition>
  </header>
</template>
